import { browser } from "$app/env";
import { toast } from "svelte-sonner";
import type { Notification } from "#lib/api-client/types.gen.js";
import { notificationAction } from "./action-url";
import { userSettings } from "#lib/stores/userSettings.svelte.js";
import {
	DESKTOP_NOTIFICATION_SETTING,
	showDesktopNotification,
	shouldPresentDesktopNotification,
} from "./browser-notifications";

/** CSS class that gives a loud notification its bouncing entrance/loop. */
export const LOUD_TOAST_CLASS = "notification-loud";

let audioContext: AudioContext | null = null;

/**
 * Plays the short two-tone chime used by loud notifications.
 *
 * Browsers only allow audio after a user gesture; until then `AudioContext`
 * starts suspended and this is a silent no-op (a hard requirement for not
 * throwing on the first notification of a fresh session). Any audio failure is
 * swallowed for the same reason.
 */
export function playNotificationSound(): void {
	if (!browser) return;
	try {
		const Ctor =
			window.AudioContext ??
			(window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
		if (!Ctor) return;
		audioContext ??= new Ctor();
		if (audioContext.state === "suspended") {
			void audioContext.resume();
		}
		const context = audioContext;
		const now = context.currentTime;
		const notes = [880, 1320];
		notes.forEach((frequency, index) => {
			const start = now + index * 0.12;
			const oscillator = context.createOscillator();
			const gain = context.createGain();
			oscillator.type = "sine";
			oscillator.frequency.value = frequency;
			gain.gain.setValueAtTime(0.0001, start);
			gain.gain.exponentialRampToValueAtTime(0.15, start + 0.02);
			gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.18);
			oscillator.connect(gain);
			gain.connect(context.destination);
			oscillator.start(start);
			oscillator.stop(start + 0.2);
		});
	} catch {
		// Audio is a nicety; never let it break notification delivery.
	}
}

/**
 * Hands a notification to the host OS when the user enabled desktop
 * notifications and is not currently looking at the app (hidden tab or
 * unfocused window). Quiet notifications stay in the bell only.
 */
function presentDesktopNotification(notification: Notification): void {
	if (notification.delivery === "quiet") return;
	if (userSettings.get<boolean>(DESKTOP_NOTIFICATION_SETTING, false) !== true) return;
	if (!shouldPresentDesktopNotification()) return;
	void showDesktopNotification(notification);
}

/**
 * Presents a newly received notification according to its delivery level:
 *   quiet  - nothing beyond the bell/unread badge (the store handles that)
 *   normal - a standard top-right toast
 *   loud   - a bouncing toast with a sound and an action button
 */
export function presentNotification(notification: Notification): void {
	const action = notificationAction(notification);
	switch (notification.delivery) {
		case "quiet":
			return;
		case "loud":
			playNotificationSound();
			presentDesktopNotification(notification);
			toast(notification.title, {
				description: notification.message,
				action,
				class: LOUD_TOAST_CLASS,
				duration: 24_000,
			});
			return;
		default:
			presentDesktopNotification(notification);
			toast(notification.title, {
				description: notification.message,
				action,
				duration: 12_000,
			});
	}
}
